
import Vue from "vue";

import ImageView from "@/components/atoms/ImageView.vue";
import {
  getTranslatedGenreList,
  getTranslatedCategoryList,
  getTranslatedWorkStatusList,
  getTranslatedGenreById,
  getTranslatedCategoryById,
  getTranslatedWorkStatusById,
} from "@/lib/utils/novelSettingsHelper";
import { Novel, User } from "@/lib/models";
import NovelListDialog, { NovelListDialogProps } from "@/components/ui/nolaNovel/NovelListDialog.vue";
import { Dialog } from "@/lib/utils";
import NolaNovelDelinkConfirm, {
  NolaNovelDelinkConfirmProps,
} from "@/components/ui/nolaNovel/NolaNovelDelinkConfirm.vue";
import NolaNovelUpdateConfirm, {
  NolaNovelUpdateConfirmProps,
} from "@/components/ui/nolaNovel/NolaNovelUpdateConfirm.vue";
import CupertinoAlertDialog, { CupertinoAlertDialogProps } from "@/components/ui/dialogs/CupertinoAlertDialog.vue";
import { UserClient } from "@/lib/clients";
import NolaNovelIntroduction from "@/components/ui/nolaNovel/NolaNovelIntroduction.vue";
import NovelDeleteConfirm, { NovelDeleteConfirmProps } from "@/components/ui/novel/NovelDeleteConfirm.vue";
import { NolaNovelUrlGenerator } from "@/lib/utils/generator/nolanovelUrl";
import { createUrlWithUtmParams } from "@/lib/utils/url";
import { NolaItemId, NolaPageName } from "@/lib/utils/analytics";
import AgentPromotionDialog from "@/components/ui/AgentPromotionDialog.vue";

type NovelParam = {
  id: string;
  name: string;
  image: string;
};

interface Data {
  hasNolaNovelAccount: boolean;
}

interface Methods {
  initialize: () => void;

  deleteButtonClick: () => void;
  delinkNolaNovelButtonClick: () => void;
  postToNolaNovelButtonClick: (isPost: boolean) => void;

  showNolaNovelListDialog: (isPost: boolean) => void;
  showExportConfirmDialog: (id: string) => void;
  showNolaNovelDelinkConfirmDialog: (id: string) => void;
  showNovelDeleteConfirmDialog: (title: string) => void;
  navigateToNolaNovelEditPage: (id: string) => void;
}

interface Computed {
  novel?: Novel;
  imageKey: string;
  genreName?: string;
  categoryName?: string;
  workStatusName?: string;
  novelIdFromNolaNovel?: string;
  navigateToNolaNovelLink?: string;
  user: User;
  isAgentFeatureNotAgreed: boolean;
  translatedGenreList: NovelParam[];
  translatedCategoryList: NovelParam[];
  translatedWorkStatusList: NovelParam[];
}

interface Props {
  novelId: string;
}

export default Vue.extend<Data, Methods, Computed, Props>({
  components: { ImageView },

  props: {
    novelId: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      hasNolaNovelAccount: false,
    };
  },

  computed: {
    novel() {
      const { novelId } = this;
      const { getters } = this.$store;
      return getters["novelModule/novel"](novelId) as Novel;
    },
    translatedGenreList() {
      return getTranslatedGenreList(this.$t);
    },
    translatedCategoryList() {
      return getTranslatedCategoryList(this.$t);
    },
    translatedWorkStatusList() {
      return getTranslatedWorkStatusList(this.$t);
    },
    imageKey() {
      const { novel } = this;
      if (!novel) {
        // return placeholder path.
        return "/img/placeholders/novel.png";
      }

      const { novelId, image } = novel;
      if (!image) {
        // return placeholder path.
        return "/img/placeholders/novel.png";
      }

      if (image && image.startsWith("file:")) {
        return `file:novels/${novelId}/cover.jpg`;
      }

      return image;
    },
    genreName() {
      const { novel } = this;
      if (!novel || !novel.genre) {
        return undefined;
      }

      return getTranslatedGenreById(this.$t, novel.genre);
    },
    categoryName() {
      const { novel } = this;
      if (!novel || !novel.category) {
        return undefined;
      }

      const names = novel.category.map((categoryId: string) => 
        getTranslatedCategoryById(this.$t, categoryId)
      ).filter(name => name !== "");
      
      return names.length > 0 ? names.reduce((acc: string, cur: string) => `${acc} / ${cur}`) : undefined;
    },
    workStatusName() {
      const { novel } = this;
      if (!novel || !novel.workStatus) {
        return this.translatedWorkStatusList[0].name;
      }

      const statusName = getTranslatedWorkStatusById(this.$t, novel.workStatus);
      return statusName || this.translatedWorkStatusList[0].name;
    },
    novelIdFromNolaNovel() {
      const { novel } = this;

      if (!novel) {
        return undefined;
      }

      const { associatedData } = novel;

      if (!associatedData) {
        return undefined;
      }

      const { nolaNovel } = associatedData;

      if (!nolaNovel) {
        return undefined;
      }

      const { id } = nolaNovel;

      if (!id) {
        return undefined;
      }

      return id;
    },
    navigateToNolaNovelLink() {
      const url = process.env.VUE_APP_NOLANOVEL_WEB;
      const { novelIdFromNolaNovel } = this;
      return createUrlWithUtmParams(
        `${url}/novel/${novelIdFromNolaNovel}`,
        NolaPageName.Detail,
        NolaItemId.LinkedNolaNovelIdLink
      );
    },
    user() {
      return this.$store.getters["userModule/user"];
    },
    isAgentFeatureNotAgreed() {
      return this.user.agentStartedAt === null;
    },
  },

  async created() {
    const { initialize } = this;
    initialize();
  },

  methods: {
    // initialize

    async initialize() {
      const { novel } = this;

      if (novel) {
        // エージェントに未同意状態で、作品完了にした場合ポップアップを表示
        if (this.isAgentFeatureNotAgreed && this.$route.query.workStatus === "completed") {
          const agentPromotionDialog = new Dialog(AgentPromotionDialog);
          agentPromotionDialog.show({ novelTitle: novel.title, useWorkCompletedDesign: true });
        }
      }

      try {
        const results = await new UserClient().checkConnectedOtherServices();
        const { nolaNovel } = results;
        if (nolaNovel) {
          this.hasNolaNovelAccount = nolaNovel.result ?? false;
        }
      } catch (e) {
        // 特にエラーでも何もしない
      }
    },

    // event handle (button, checkbox, etc)

    deleteButtonClick() {
      const { novel, showNovelDeleteConfirmDialog } = this;
      if (!novel) {
        return;
      }

      const { title } = novel;
      showNovelDeleteConfirmDialog(title);
    },
    delinkNolaNovelButtonClick() {
      const { novelIdFromNolaNovel, showNolaNovelDelinkConfirmDialog } = this;
      if (!novelIdFromNolaNovel) {
        return;
      }

      showNolaNovelDelinkConfirmDialog(novelIdFromNolaNovel);
    },
    postToNolaNovelButtonClick(isPost) {
      const { showNolaNovelListDialog, novelIdFromNolaNovel, showExportConfirmDialog } = this;

      if (novelIdFromNolaNovel) {
        showExportConfirmDialog(novelIdFromNolaNovel);
        return;
      }

      showNolaNovelListDialog(isPost);
    },

    // view

    async showNolaNovelListDialog(isPost) {
      const { novelId, hasNolaNovelAccount } = this;

      if (!hasNolaNovelAccount) {
        const introduction = new Dialog(NolaNovelIntroduction);
        introduction.show();
        return;
      }

      const novelListDialog = new Dialog(NovelListDialog);
      const data: NovelListDialogProps = {
        novelId,
        isPost,
      };

      const result = await novelListDialog.show(data);
      if (result) {
        const completeDialog = new Dialog(CupertinoAlertDialog);
        const data: CupertinoAlertDialogProps = {
          title: this.$t("novelDetail.linkComplete.title"),
          content: this.$t("novelDetail.linkComplete.content"),
          action: this.$t("novelDetail.linkComplete.closeButton"),
        };
        completeDialog.show(data);
      }

      const { initialize } = this;
      initialize();
    },
    async showExportConfirmDialog(id) {
      const { novel, navigateToNolaNovelEditPage } = this;
      if (!novel) {
        return;
      }

      const confirmDialog = new Dialog(NolaNovelUpdateConfirm);
      const data: NolaNovelUpdateConfirmProps = {
        id,
        content: this.$t("novelDetail.updateConfirm.content"),
      };
      const result = await confirmDialog.show(data);
      if (result) {
        navigateToNolaNovelEditPage(id);
      }
    },
    async showNolaNovelDelinkConfirmDialog(id) {
      const { novelId } = this;
      const deleteDialog = new Dialog(NolaNovelDelinkConfirm);
      const data: NolaNovelDelinkConfirmProps = {
        novelId,
        nolaNovelId: id,
      };

      const result = await deleteDialog.show(data);
      if (result) {
        const completeDialog = new Dialog(CupertinoAlertDialog);
        const data: CupertinoAlertDialogProps = {
          title: this.$t("novelDetail.unlinkComplete.title"),
          content: this.$t("novelDetail.unlinkComplete.content"),
          action: this.$t("novelDetail.unlinkComplete.closeButton"),
        };
        completeDialog.show(data);
      }

      const { initialize } = this;
      initialize();
    },
    async showNovelDeleteConfirmDialog(title) {
      const { novelId, novelIdFromNolaNovel } = this;
      const confirmDialog = new Dialog(NovelDeleteConfirm);
      const data: NovelDeleteConfirmProps = {
        novelId,
        nolaNovelId: novelIdFromNolaNovel,
        title,
      };

      const result = await confirmDialog.show(data);

      if (result) {
        this.$notify({
          title: this.$t("novelDetail.deleteComplete.message"),
          type: "error",
        });

        this.$router.push({ name: "home" });
      }
    },
    async navigateToNolaNovelEditPage(id: string) {
      const url = process.env.VUE_APP_NOLANOVEL_WEB;

      if (!url) {
        throw new Error(this.$t("novelDetail.nolaNovelUrlError"));
      }

      const { novelId } = this;

      const service = new NolaNovelUrlGenerator(process.env.VUE_APP_NOLANOVEL_WEB!);

      const updateNovelUrl = await service.genUpdateNovelUrl(id, novelId);
      window.open(
        createUrlWithUtmParams(updateNovelUrl, NolaPageName.Detail, NolaItemId.ConfirmOverwriteNovelButton),
        process.env.VUE_APP_NOLANOVEL_NAME
      );
    },
  },
});
